/**
 * Phase 27.50.B — Storefront (`/shop`).
 *
 * Grid of purchasable + published marketing_templates. Any authenticated
 * user can browse and add items to their cart. Personalization fields
 * (marketing_templates.fields marked `required`) are filled on the
 * product-detail modal before Add-to-Cart succeeds.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { shopAPI, marketingAPI } from '@/lib/api';
import { notifyCartUpdated } from '@/hooks/useCartCount';
import { getErrorMessage } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, ShoppingCart, Plus, Minus, Package, Search, X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import { StorefrontWelcomeBanner } from '@/components/shop/StorefrontWelcomeBanner';
import { MARKETING_CATEGORIES, CATEGORY_LABEL_BY_VALUE } from '@/constants/marketingCategories';
import { MarkdownDescription } from '@/components/common/MarkdownEditor';

function ProductThumb({ product }) {
  // Phase 27.50.C.34 — Hero image priority mirrors ProductGalleryViewer
  // so the grid card and the product dialog always start on the same
  // image (cover → first gallery image → sample page 1 → placeholder).
  const templateId = product?.template_id;
  const heroKind = product?.cover_image_path
    ? 'cover'
    : (product?.gallery_images?.[0]?.image_id ? 'gallery' : 'preview');
  const heroId = heroKind === 'gallery' ? product.gallery_images[0].image_id : null;
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!templateId) return undefined;
    let revoke;
    let loader;
    if (heroKind === 'cover') loader = marketingAPI.coverImageBlob(templateId);
    else if (heroKind === 'gallery') loader = marketingAPI.galleryImageBlob(templateId, heroId);
    else loader = marketingAPI.previewBlob(templateId, 1);
    loader.then((r) => {
      const u = URL.createObjectURL(r.data); revoke = u; setSrc(u);
    }).catch(() => {
      // Fall back to sample page 1 if the preferred hero fails so the
      // grid tile never renders blank.
      if (heroKind !== 'preview') {
        marketingAPI.previewBlob(templateId, 1).then((r) => {
          const u = URL.createObjectURL(r.data); revoke = u; setSrc(u);
        }).catch(() => setSrc(null));
      } else {
        setSrc(null);
      }
    });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [templateId, heroKind, heroId]);
  if (!src) return (
    <div className="h-52 rounded-md bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-slate-300">
      <Package size={28} />
    </div>
  );
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      className="h-52 w-full object-contain rounded-md bg-white border border-slate-100"
      style={{ imageRendering: 'auto' }}
    />
  );
}

export default function ShopPage({ embedded = false }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [active, setActive] = useState(null);
  // Phase 27.50.B.19 — Category filter chips. `null` = show all.
  const [category, setCategory] = useState(null);
  // Phase 27.50.B.20 — Title/SKU search box. Composes with the
  // category filter above (both apply at once).
  const [search, setSearch] = useState('');
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [ps, c] = await Promise.all([shopAPI.listProducts(), shopAPI.getCart()]);
      setProducts(ps.data.items || []);
      setCartCount((c.data.items || []).reduce((s, i) => s + i.quantity, 0));
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load storefront'));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Only surface categories that have at least one live product so
  // shoppers don't see empty chips. Category counts stay based on the
  // full product set — the search box narrows what's shown but never
  // hides the chips themselves so shoppers can pivot easily.
  const availableCategories = MARKETING_CATEGORIES.filter(
    (c) => products.some((p) => p.category === c.value),
  );
  const q = search.trim().toLowerCase();
  const filtered = products.filter((p) => {
    if (category && p.category !== category) return false;
    if (!q) return true;
    const haystack = `${p.title || ''} ${p.sku || ''} ${p.description || ''}`.toLowerCase();
    return haystack.includes(q);
  });

  const wrapperClass = embedded ? '' : 'max-w-6xl mx-auto p-6';

  return (
    <div className={wrapperClass} data-testid="shop-page">
      <StorefrontWelcomeBanner />
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Shop</h1>
            <p className="text-sm text-slate-500 mt-1">
              Browse marketing materials and add them to your cart.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav('/cart')}
            data-testid="shop-open-cart"
            className="gap-2"
          >
            <ShoppingCart size={16} />
            Cart
            {cartCount > 0 && (
              <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid="shop-cart-count">
                {cartCount}
              </Badge>
            )}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading products…
        </div>
      ) : products.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-slate-500">
            No products in the storefront yet. Ask an admin to publish some.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="shop-filters">
            {availableCategories.length > 1 && (
              <div className="flex flex-wrap items-center gap-2" data-testid="shop-category-filter">
                <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-1">Filter</span>
                <CategoryChip
                  label={`All (${products.length})`}
                  active={category === null}
                  onClick={() => setCategory(null)}
                  testId="shop-category-chip-all"
                />
                {availableCategories.map((c) => {
                  const count = products.filter((p) => p.category === c.value).length;
                  return (
                    <CategoryChip
                      key={c.value}
                      label={`${c.label} (${count})`}
                      active={category === c.value}
                      onClick={() => setCategory(c.value)}
                      testId={`shop-category-chip-${c.value}`}
                    />
                  );
                })}
              </div>
            )}
            <div className="relative ml-auto w-full sm:w-64" data-testid="shop-search-box">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products…"
                className="w-full h-9 pl-9 pr-8 rounded-full border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400"
                data-testid="shop-search-input"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center"
                  data-testid="shop-search-clear"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
          {filtered.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-10 text-center text-slate-500 space-y-2">
                {search ? (
                  <p>
                    No products matching “<span className="font-semibold text-slate-700">{search}</span>”
                    {category ? <> in {CATEGORY_LABEL_BY_VALUE[category]}</> : null}.
                  </p>
                ) : (
                  <p>No {CATEGORY_LABEL_BY_VALUE[category] || 'products'} yet.</p>
                )}
                <button
                  type="button"
                  onClick={() => { setCategory(null); setSearch(''); }}
                  className="text-xs text-emerald-700 underline underline-offset-2"
                  data-testid="shop-filters-clear"
                >
                  Show all products
                </button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                 data-testid="shop-product-grid">
              {filtered.map((p) => (
            <Card key={p.template_id}
                   className="hover:shadow-lg transition-shadow group cursor-pointer"
                   onClick={() => setActive(p)}
                   data-testid={`shop-product-card-${p.template_id}`}>
              <CardContent className="p-4 space-y-3">
                <ProductThumb product={p} />
                <div>
                  <h3 className="font-semibold text-slate-900 line-clamp-1">{p.title}</h3>
                  <p className="text-xs text-slate-500 line-clamp-2 mt-1 min-h-[2rem]">
                    {stripMarkdown(p.description) || '—'}
                  </p>
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <span className="text-lg font-bold text-slate-900">
                        ${Number(p.unit_price || 0).toFixed(2)}
                      </span>
                      {(p.pricing_tiers || []).length > 0 && (
                        <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                          Bulk pricing
                        </span>
                      )}
                    </div>
                    <Button size="sm" data-testid={`shop-open-${p.template_id}`}>
                      <Plus size={13} className="mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
            </div>
          )}
        </>
      )}

      <ProductDialog
        product={active}
        onClose={() => setActive(null)}
        onAdded={() => { setActive(null); load(); toast.success('Added to cart'); }}
      />
    </div>
  );
}

// Resolve tier-aware line total on the client side to preview accurately.
function resolveLinePrice(product, quantity) {
  const qty = Number(quantity) || 0;
  const tiers = product?.pricing_tiers || [];
  const matched = tiers.find((t) => Number(t.quantity) === qty && Number(t.price) >= 0);
  if (matched) {
    return { lineTotal: Number(matched.price), source: 'tier', tier: matched };
  }
  const unit = Number(product?.unit_price || 0);
  return { lineTotal: unit * qty, source: 'unit', tier: null };
}

function ProductDialog({ product, onClose, onAdded }) {
  const [qty, setQty] = useState(1);
  const [pers, setPers] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setQty(1);
    const seed = {};
    (product?.fields || []).forEach((f) => { seed[f.key] = ''; });
    setPers(seed);
  }, [product]);

  if (!product) return null;
  const requiredFields = (product.fields || []).filter((f) => f.required);
  const missing = requiredFields.filter((f) => !(pers[f.key] || '').trim());
  const tiers = product.pricing_tiers || [];
  const { lineTotal, source, tier } = resolveLinePrice(product, qty);

  const submit = async () => {
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      await shopAPI.addItem({
        template_id: product.template_id,
        quantity: qty,
        personalization: pers,
      });
      notifyCartUpdated();
      onAdded();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to add to cart'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] p-0 gap-0 flex flex-col"
        data-testid="shop-product-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
          <DialogTitle>{product.title}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="shop-product-dialog-body">
          <ProductGalleryViewer product={product} />
          {product.description ? (
            <MarkdownDescription source={product.description} />
          ) : (
            <p className="text-sm text-slate-400 italic">No description yet.</p>
          )}

          {/* Pricing tier picker — one click sets both qty + preview. */}
          {tiers.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-2">
                Bulk pricing
              </p>
              <div className="flex flex-wrap gap-2">
                {tiers.map((t) => {
                  const active = Number(t.quantity) === qty;
                  return (
                    <button
                      key={t.quantity}
                      type="button"
                      onClick={() => setQty(Number(t.quantity))}
                      className={
                        'text-left px-3 py-2 rounded-md border transition ' +
                        (active
                          ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200'
                          : 'border-slate-200 hover:border-slate-400')
                      }
                      data-testid={`tier-btn-${t.quantity}`}
                    >
                      <div className="text-xs text-slate-500">{t.quantity} units</div>
                      <div className="text-sm font-bold text-slate-900">
                        ${Number(t.price).toFixed(2)}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        ${(Number(t.price) / Number(t.quantity)).toFixed(2)} ea
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold" data-testid="shop-line-total">
                ${lineTotal.toFixed(2)}
              </div>
              {source === 'tier' && tier && (
                <div className="text-[11px] text-emerald-700 mt-0.5">
                  Bulk tier: {tier.quantity} units → ${Number(tier.price).toFixed(2)}
                </div>
              )}
              {source === 'unit' && Number(product.unit_price || 0) > 0 && (
                <div className="text-[11px] text-slate-500 mt-0.5">
                  ${Number(product.unit_price).toFixed(2)} × {qty}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 border rounded-md">
              <Button size="sm" variant="ghost"
                       onClick={() => setQty((q) => Math.max(1, q - 1))}
                       data-testid="qty-minus">
                <Minus size={13} />
              </Button>
              <span className="px-3 text-sm font-mono" data-testid="qty-value">{qty}</span>
              <Button size="sm" variant="ghost"
                       onClick={() => setQty((q) => q + 1)}
                       data-testid="qty-plus">
                <Plus size={13} />
              </Button>
            </div>
          </div>
          {(product.fields || []).length > 0 && (
            <div className="border-t pt-3 space-y-2">
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-400">
                Personalization
              </p>
              {(product.fields || []).map((f) => (
                <div key={f.key}>
                  <Label className="text-xs">
                    {f.label} {f.required && <span className="text-rose-500">*</span>}
                  </Label>
                  <Input
                    value={pers[f.key] || ''}
                    onChange={(e) => setPers({ ...pers, [f.key]: e.target.value })}
                    placeholder={f.helper_text}
                    data-testid={`personalize-${f.key}`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-slate-100 bg-white shrink-0 rounded-b-lg">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="add-to-cart-btn">
            {saving && <Loader2 size={14} className="mr-1 animate-spin" />}
            Add ${lineTotal.toFixed(2)} to Cart
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * ProductGalleryViewer — Big preview + clickable thumbnail strip.
 *
 * Combines the cover image (index 0), every gallery_image, and every
 * preview_page of the uploaded sample file. Preview pages are how
 * multi-page PDFs surface all their pages in the shop — the admin
 * uploads a two-page business card sample and both faces show up.
 * If nothing is available, the standard product thumb placeholder
 * still renders.
 */
function ProductGalleryViewer({ product }) {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const slots = [];
  if (product?.cover_image_path) {
    slots.push({ key: 'cover', kind: 'cover' });
  }
  for (const g of product?.gallery_images || []) {
    if (g?.image_id) slots.push({ key: `g:${g.image_id}`, kind: 'gallery', imageId: g.image_id });
  }
  // Phase 27.50.C.33 — Include every page of the uploaded sample file
  // as an additional gallery slot. This is how multi-page PDFs (front /
  // back business cards, brochures, etc.) actually appear on the shop
  // page. Previously only page 1 rendered via ProductThumb fallback.
  for (const p of product?.preview_pages || []) {
    const pageNo = Number(p?.page);
    if (Number.isFinite(pageNo) && pageNo > 0) {
      slots.push({ key: `p:${pageNo}`, kind: 'preview', pageNo });
    }
  }
  if (slots.length === 0) {
    return <ProductThumb product={product} />;
  }
  const activeIdx = Math.min(active, slots.length - 1);
  const current = slots[activeIdx];
  const goPrev = () => setActive((i) => (i - 1 + slots.length) % slots.length);
  const goNext = () => setActive((i) => (i + 1) % slots.length);
  return (
    <div className="space-y-2" data-testid="product-gallery-viewer">
      <button
        type="button"
        onClick={() => setZoom(true)}
        aria-label="Zoom image"
        data-testid="gallery-open-zoom"
        className="group relative h-80 w-full rounded-md border border-slate-100 bg-white flex items-center justify-center overflow-hidden cursor-zoom-in"
      >
        <GallerySlotImage templateId={product.template_id} slot={current} />
        <span className="absolute top-2 right-2 rounded-full bg-slate-900/70 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn size={14} />
        </span>
      </button>
      {slots.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="product-gallery-strip">
          {slots.map((s, idx) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setActive(idx)}
              data-testid={`gallery-thumb-btn-${idx}`}
              className={`h-14 w-14 shrink-0 rounded-md border overflow-hidden bg-white flex items-center justify-center transition ${
                idx === active ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-slate-400'
              }`}
            >
              <GallerySlotImage templateId={product.template_id} slot={s} thumb />
            </button>
          ))}
        </div>
      )}
      <ImageZoomModal
        open={zoom}
        onClose={() => setZoom(false)}
        templateId={product.template_id}
        slots={slots}
        activeIdx={activeIdx}
        onPrev={goPrev}
        onNext={goNext}
      />
    </div>
  );
}

/**
 * ImageZoomModal — Full-screen fixed overlay that renders the current
 * gallery slot at native resolution so shoppers can read fine print
 * (business-card back copy, terms, tiny disclaimers) without leaving
 * the storefront. Keyboard: ← / → cycle slots, Esc closes.
 */
function ImageZoomModal({ open, onClose, templateId, slots, activeIdx, onPrev, onNext }) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, onPrev, onNext]);
  if (!open || !slots?.length) return null;
  const slot = slots[activeIdx];
  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      data-testid="image-zoom-modal"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close zoom"
        data-testid="image-zoom-close"
        className="absolute top-4 right-4 text-white/70 hover:text-white rounded-full p-2 bg-white/10 hover:bg-white/20 transition"
      >
        <X size={22} />
      </button>
      {slots.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            aria-label="Previous"
            data-testid="image-zoom-prev"
            className="absolute left-4 md:left-8 text-white/70 hover:text-white rounded-full p-2 bg-white/10 hover:bg-white/20 transition"
          >
            <ChevronLeft size={26} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            aria-label="Next"
            data-testid="image-zoom-next"
            className="absolute right-4 md:right-8 text-white/70 hover:text-white rounded-full p-2 bg-white/10 hover:bg-white/20 transition"
          >
            <ChevronRight size={26} />
          </button>
        </>
      )}
      <div
        className="max-w-[95vw] max-h-[92vh] p-2 flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        data-testid="image-zoom-canvas"
      >
        <ZoomedSlotImage templateId={templateId} slot={slot} />
      </div>
      {slots.length > 1 && (
        <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs font-mono bg-white/10 rounded-full px-3 py-1">
          {activeIdx + 1} / {slots.length}
        </span>
      )}
    </div>
  );
}

function ZoomedSlotImage({ templateId, slot }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    let loader;
    if (slot.kind === 'gallery') loader = marketingAPI.galleryImageBlob(templateId, slot.imageId);
    else if (slot.kind === 'preview') loader = marketingAPI.previewBlob(templateId, slot.pageNo);
    else loader = marketingAPI.coverImageBlob(templateId);
    loader.then((r) => { const u = URL.createObjectURL(r.data); revoke = u; setSrc(u); })
      .catch(() => setSrc(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [templateId, slot.key]);
  if (!src) return <Loader2 size={28} className="animate-spin text-white/60" />;
  return (
    <img
      src={src}
      alt=""
      decoding="async"
      className="max-w-full max-h-[88vh] object-contain rounded"
    />
  );
}

function GallerySlotImage({ templateId, slot, thumb = false }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    let loader;
    if (slot.kind === 'gallery') {
      loader = marketingAPI.galleryImageBlob(templateId, slot.imageId);
    } else if (slot.kind === 'preview') {
      loader = marketingAPI.previewBlob(templateId, slot.pageNo);
    } else {
      loader = marketingAPI.coverImageBlob(templateId);
    }
    loader.then((r) => { const u = URL.createObjectURL(r.data); revoke = u; setSrc(u); })
      .catch(() => setSrc(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [templateId, slot.key]);
  if (!src) return <Package size={thumb ? 16 : 28} className="text-slate-300" />;
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      className="max-w-full max-h-full object-contain"
    />
  );
}


function CategoryChip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1 h-7 px-3 rounded-full text-xs font-medium border transition ${
        active
          ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50'
      }`}
    >
      {label}
    </button>
  );
}

/**
 * Strip common markdown decorations for card previews. We keep the
 * source markdown intact server-side; this only sanitizes the
 * two-line summary rendered on the shop grid tiles.
 */
function stripMarkdown(md) {
  if (!md) return '';
  return md
    .replace(/^#{1,6}\s+/gm, '')  // headings
    .replace(/\*\*(.*?)\*\*/g, '$1')  // bold
    .replace(/\*(.*?)\*/g, '$1')  // italic
    .replace(/`([^`]+)`/g, '$1')  // inline code
    .replace(/^[-*+]\s+/gm, '• ')  // bullets
    .replace(/\n{2,}/g, ' · ')  // paragraph breaks
    .replace(/\n/g, ' ')
    .trim();
}
